'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, ExternalLink, X } from 'lucide-react';
import { authenticatedFetch } from '@/lib/authenticated-fetch';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderData: any;
}

const DATA_URL_RE = /^data:image\//;

export default function QrCodeModal({ isOpen, onClose, orderData }: Props) {
  const [qrString, setQrString] = React.useState<string>('');
  const [timeLeft, setTimeLeft] = React.useState<number | null>(null);
  const [isExpired, setIsExpired] = React.useState(false);

  const isCryptoPayment = orderData?.gateway === 'CEYPAY';

  React.useEffect(() => {
    if (!orderData?.qrCode) return;

    setIsExpired(false);
    setTimeLeft(null);

    const rawQrCode = orderData.qrCode;
    let initialQr = '';
    if (typeof rawQrCode === 'string') {
      initialQr = rawQrCode;
    } else if (rawQrCode && typeof rawQrCode === 'object' && rawQrCode.url) {
      initialQr = rawQrCode.url;
    } else if (rawQrCode && typeof rawQrCode === 'object') {
      initialQr = rawQrCode.value || rawQrCode.data || JSON.stringify(rawQrCode);
    }
    if (initialQr && initialQr !== '{}') {
      setQrString(initialQr);
    }
  }, [orderData]);

  React.useEffect(() => {
    if (!orderData?.createdAt || isExpired) return;

    const createdAt = new Date(orderData.createdAt).getTime();
    const expiryTime = createdAt + 5 * 60 * 1000;

    const timer = setInterval(() => {
      const now = Date.now();
      const diff = Math.floor((expiryTime - now) / 1000);

      if (diff <= 0) {
        setTimeLeft(0);
        setIsExpired(true);
        clearInterval(timer);
      } else {
        setTimeLeft(diff);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [orderData, isExpired]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const hasPaymentUrl = !!orderData?.paymentUrl;

  React.useEffect(() => {
    if (!isOpen || !orderData?.id) return;

    const checkStatus = async () => {
      try {
        const resp = await authenticatedFetch(`${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001'}/payment/public-status/${orderData.id}`);
        if (!resp.ok) return;
        const result = await resp.json();
        const data = result.data || result;

        if (
          data.status === 'CONFIRMED' ||
          data.status === 'PAID' ||
          (data.order && (data.order.status === 'COMPLETED' || data.order.status === 'PROCESSING'))
        ) {
          window.location.href = `/payment-status?id=${orderData.id}`;
        } else if (data.qrCode && typeof data.qrCode === 'string' && (!qrString || qrString === '{}')) {
           setQrString(data.qrCode);
        } else if (data.qrCode && typeof data.qrCode === 'object' && data.qrCode.url && (!qrString || qrString === '{}')) {
           setQrString(data.qrCode.url);
        }
      } catch (e) {
        console.error('Polling error', e);
      }
    };

    const intervalId = setInterval(checkStatus, 2000);
    return () => clearInterval(intervalId);
  }, [isOpen, orderData?.id, qrString]);

  const handleOpenCheckout = () => {
    if (orderData?.paymentUrl) {
      window.open(orderData.paymentUrl, '_blank');
    }
  };

  const showQrOnly = !hasPaymentUrl || !!qrString;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 font-sans">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative w-full max-w-[360px] bg-white rounded-xl shadow-xl overflow-hidden border border-slate-200 text-center"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <QrCode className="w-4 h-4 text-emerald-600" />
                {isCryptoPayment ? 'Crypto Payment' : 'Lanka QR Payment'}
              </h2>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 bg-white hover:bg-slate-100 rounded-full w-7 h-7 flex items-center justify-center transition-colors shadow-sm border border-slate-200"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="p-6">
              {isCryptoPayment ? (
                <p className="text-xs text-slate-600 font-medium mb-4 px-2">
                  Scan the QR code with your crypto wallet or open the checkout link
                </p>
              ) : (
                <p className="text-xs text-slate-600 font-medium mb-4 px-2">
                  LANKA QR - ඕනෑම Bank Acc එකකින් QR එක Scan කර ගෙවීම සිදු කල හැක.
                </p>
              )}

              <div className="mb-6 flex justify-center">
                <div className="border border-slate-200 bg-slate-50/50 rounded-lg px-3 py-1.5 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Expires In:</span>
                    <span className="text-sm font-mono font-bold text-slate-700">{timeLeft !== null ? formatTime(timeLeft) : '05:00'}</span>
                </div>
              </div>

              {showQrOnly ? (
                <div className="bg-slate-50 p-2 rounded-xl border border-slate-200 inline-flex shadow-sm w-[196px] h-[196px] items-center justify-center">
                  {(!qrString || qrString === '{}') ? (
                    <div className="flex flex-col items-center justify-center space-y-3 opacity-50">
                      <svg className="animate-spin h-6 w-6 text-slate-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                      <span className="text-xs font-semibold text-slate-600">Generating QR...</span>
                    </div>
                  ) : DATA_URL_RE.test(qrString) ? (
                    <div className="bg-white p-2 rounded-lg">
                      <img src={qrString} alt="QR Code" className="w-[180px] h-[180px]" />
                    </div>
                  ) : (
                    <div className="bg-white p-2 rounded-lg">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrString)}&size=300x300`}
                        alt="QR Code"
                        className="w-[180px] h-[180px]"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 shadow-sm w-full">
                    <div className="text-xs text-slate-500 font-medium mb-2">Checkout Link</div>
                    <div className="bg-white rounded-lg border border-slate-100 p-3 break-all text-xs text-slate-700 font-mono">
                      {orderData?.paymentUrl}
                    </div>
                  </div>
                  <button
                    onClick={handleOpenCheckout}
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg active:scale-[0.98] text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Checkout
                  </button>
                </div>
              )}

              {hasPaymentUrl && !!qrString && (
                <div className="mt-3">
                  <button
                    onClick={handleOpenCheckout}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-semibold underline"
                  >
                    Or open checkout link instead
                  </button>
                </div>
              )}

              <div className="mt-5 flex flex-col items-center gap-2">
                <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100 shadow-sm">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span className="text-[11px] font-bold uppercase tracking-wider">Waiting for payment...</span>
                </div>
                <p className="text-[10px] text-slate-400 font-medium">Redirecting automatically once paid</p>
              </div>

              {!isCryptoPayment && (
                <>
                  <p className="text-[11px] text-rose-500 font-bold mt-4 px-2">
                    Note: ComBank Q currently unavailable
                  </p>

                  <div className="mt-4 px-4">
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.15em] mb-3">Supported Apps</p>
                    <div className="flex flex-wrap items-center justify-center gap-2 grayscale-[0.3] opacity-80">
                      <img src="/assets/apps/genie.webp" alt="Genie" className="h-8 w-auto object-contain rounded-md" />
                      <img src="/assets/apps/frimi.webp" alt="FriMi" className="h-8 w-auto object-contain rounded-md" />
                      <img src="/assets/apps/solo.webp" alt="Solo" className="h-8 w-auto object-contain rounded-md" />
                      <img src="/assets/apps/flex.webp" alt="Flex" className="h-8 w-auto object-contain rounded-md" />
                      <img src="/assets/apps/peoplespay.webp" alt="PeoplesPay" className="h-8 w-auto object-contain rounded-md" />
                      <img src="/assets/apps/wepay.webp" alt="WePay" className="h-8 w-auto object-contain rounded-md" />
                    </div>
                  </div>
                </>
              )}

              {isExpired && (
                <div className="mt-4 bg-rose-50 text-rose-600 border border-rose-200 px-3 py-2.5 rounded-lg text-xs font-bold leading-relaxed">
                  Payment Expired. Please close and try again.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50">
              <p className="text-[10px] text-slate-400 italic">Do not close this window until redirection</p>
            </div>

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
